<template>
  <div class="relative select-none" ref="dropdownRef">
    <!-- Dropdown button -->
    <button
      ref="buttonRef"
      @click="toggleDropdown"
      class="font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center text-neutral-800 bg-neutral-100 hover:bg-neutral-200"
      type="button"
    >
      <!-- User initials for small screens -->
      <span class="md:hidden w-7">{{ initials }}</span>
      <!-- User display name for larger screens -->
      <span class="truncate max-w-[120px] hidden md:inline">{{
        displayName
      }}</span>
      <!-- Dropdown arrow icon -->
      <svg
        class="w-2.5 h-2.5 ms-3"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>

    <!-- Dropdown menu -->
    <div
      v-if="isOpen"
      ref="dropdownMenuRef"
      class="border-solid border-neutral-300 border absolute right-0 z-40 mt-3 bg-white divide-y divide-neutral-100 rounded-lg shadow-lg w-[190px]"
    >
      <ul class="p-2 overflow-y-auto text-sm max-h-48 text-neutral-700">
        <!-- Admin user management link -->
        <li v-if="authStore.isAdmin">
          <a
            href="https://provstitoender.dk/wp-admin/users.php"
            class="flex items-center px-2 py-2 rounded cursor-pointer hover:bg-neutral-100 text-neutral-700"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="mr-2">Brugeradministration</span>
            <ExternalLink class="-mt-0.5" :size="15" color="#3d3d3d" />
          </a>
        </li>
        <!-- Logout link -->
        <li>
          <a
            href="#"
            class="flex items-center px-2 py-2 rounded cursor-pointer hover:bg-neutral-100 text-neutral-700"
            @click="handleLogoutClick"
          >
            Log ud
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  defineProps,
  onMounted,
  onBeforeUnmount,
  inject,
} from "vue";
import { ExternalLink } from "lucide-vue-next";

// Define props
const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

// Inject auth store
const authStore = inject("authStore");
const usersStore = inject("usersStore");

// Reactive state
const isOpen = ref(false);
const dropdownRef = ref(null);
const buttonRef = ref(null);
const dropdownMenuRef = ref(null);

// Computed property for display name
const displayName = computed(() => {
  if (!props.user) return "Bruger";
  if (props.user.first_name && props.user.last_name) {
    return `${props.user.first_name} ${props.user.last_name}`.trim();
  } else if (props.user.first_name) {
    return props.user.first_name.trim();
  } else if (props.user.last_name) {
    return props.user.last_name.trim();
  } else if (props.user.roles && props.user.roles.length > 0) {
    return (
      props.user.roles[0].charAt(0).toUpperCase() + props.user.roles[0].slice(1)
    );
  } else {
    return "Bruger";
  }
});

// Computed property for user initials
const initials = computed(() => usersStore.getUserInitials(props.user));

// Method to toggle dropdown visibility
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

// Method to close dropdown
const closeDropdown = () => {
  isOpen.value = false;
};

// Method to handle logout click
const handleLogoutClick = async () => {
  await authStore.logout();
  closeDropdown();
};

// Method to handle outside click
const handleOutsideClick = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    closeDropdown();
  }
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);
});
</script>